import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { ChatCircle, Users, Lightning, Sparkle } from "@phosphor-icons/react";

const PLAN_BADGE_COLOR = {
  free: "bg-gray-200",
  trial: "bg-[#FFD166]",
  active: "bg-[#06D6A0] text-white",
  payment_failed: "bg-[#FF6B6B] text-white",
  cancelled: "bg-gray-400 text-white",
  expired: "bg-gray-500 text-white",
};

const Bar = ({ used, total, warn }) => {
  const pct = Math.min(100, total ? Math.round((used / total) * 100) : 0);
  const color = warn ? "#FF6B6B" : pct >= 80 ? "#FFD166" : "#06D6A0";
  return (
    <div className="w-full h-3 bg-black/10 border-2 border-black rounded-sm overflow-hidden">
      <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
};

export default function UsageMeter() {
  const [state, setState] = useState(null);

  useEffect(() => {
    api
      .get("/billing/me")
      .then((r) => setState(r.data))
      .catch(() => setState(null));
  }, []);

  if (!state) return null;

  const plan = state.plan || "free";
  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
  const status = state.plan_status || "free";
  const limits = state.limits || {};
  const usage = state.usage || {};
  const custAtLimit = usage.customers >= limits.customers;
  const waAtLimit = usage.wa_msgs >= limits.wa_msgs;

  return (
    <div className="nb-card p-5" data-testid="usage-meter">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#4361EE] text-white border-2 border-black rounded-md flex items-center justify-center">
            <Lightning size={18} weight="bold" />
          </div>
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4A4A]">Your plan & usage</p>
            <div className="flex items-center gap-2 mt-1">
              <h3 className="font-heading text-xl font-black">{planLabel}</h3>
              <span
                data-testid="plan-status-badge"
                className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-sm border-2 border-black ${PLAN_BADGE_COLOR[status] || "bg-gray-200"}`}
              >
                {status.replace("_", " ")}
              </span>
            </div>
            {status === "trial" && state.trial_ends_at && (
              <p className="text-xs text-[#4A4A4A] mt-0.5 font-semibold">
                Free trial ends {new Date(state.trial_ends_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </p>
            )}
            {status === "payment_failed" && state.grace_until && (
              <p className="text-xs text-[#FF6B6B] mt-0.5 font-bold">
                Payment failed — features restricted after {new Date(state.grace_until).toLocaleDateString("en-IN")}
              </p>
            )}
          </div>
        </div>
        {plan !== "pro" && (
          <Link
            to="/pricing"
            data-testid="upgrade-cta"
            className="nb-btn bg-[#4361EE] text-white px-4 py-2 text-sm flex items-center gap-1.5"
          >
            <Sparkle size={14} weight="bold" />
            {plan === "free" ? "Start free trial" : "Upgrade"}
          </Link>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div data-testid="usage-customers">
          <div className="flex items-center justify-between mb-1.5">
            <span className="flex items-center gap-1.5 text-sm font-bold">
              <Users size={16} weight="bold" />
              Customers
            </span>
            <span className={`text-sm font-black ${custAtLimit ? "text-[#FF6B6B]" : ""}`}>
              {usage.customers} / {limits.customers}
            </span>
          </div>
          <Bar used={usage.customers} total={limits.customers} warn={custAtLimit} />
          {custAtLimit && (
            <p className="text-xs text-[#FF6B6B] mt-1.5 font-bold">
              Customer limit reached. <Link to="/pricing" className="underline">Upgrade to add more</Link>
            </p>
          )}
        </div>
        <div data-testid="usage-whatsapp">
          <div className="flex items-center justify-between mb-1.5">
            <span className="flex items-center gap-1.5 text-sm font-bold">
              <ChatCircle size={16} weight="bold" />
              WhatsApp this cycle
            </span>
            <span className={`text-sm font-black ${waAtLimit ? "text-[#FF6B6B]" : ""}`}>
              {usage.wa_msgs} / {limits.wa_msgs}
            </span>
          </div>
          <Bar used={usage.wa_msgs} total={limits.wa_msgs} warn={waAtLimit} />
          {waAtLimit && (
            <p className="text-xs text-[#FF6B6B] mt-1.5 font-bold">
              Monthly WhatsApp limit reached. <Link to="/pricing" className="underline">Upgrade for more</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
